import type { ComponentPropsWithoutRef, ElementType, JSX } from 'react';

import classNames from 'classnames';

import styles from './Avatar.module.css';

type AsProp<T extends ElementType> = ComponentPropsWithoutRef<T> & {
  as?: T;
};

export const SizeOptions = [
  'tiny',
  'xsmall',
  'small',
  'medium',
  'large',
  'xlarge',
  'xxlarge',
  'xxxlarge',
] as const;
export type SizeOption = (typeof SizeOptions)[number];

export const Variants = ['rounded', 'square'] as const;
export type Variant = (typeof Variants)[number];

export const Kinds = ['default', 'grey', 'orange'] as const;
export type Kind = (typeof Kinds)[number];

interface UserAvatarProps {
  imageSrc?: string | null;
  fallbackImageSrc?: string;
  name: string | null;
  size?: SizeOption;
  variant?: Variant;
  kind?: Kind;
}

/**
 * "PS" for "Priya Shah", "AN" for "Ana", "ÉM" for "Élodie Martin". Keeps
 * letters and digits from any script and drops punctuation, but keeps the
 * spaces: they are what separates the first name from the last.
 */
export function getInitials(name?: string | null): string {
  const words = (name ?? '')
    .replace(/[^\p{L}\p{N}\s]/gu, '')
    .split(/\s+/)
    .filter(Boolean);
  const letters = words.length >= 2 ? [words[0], words[words.length - 1]] : [...(words[0] ?? '')];
  return letters
    .slice(0, 2)
    .map((word) => [...word][0]?.toUpperCase() ?? '')
    .join('');
}

function AvatarImage<T extends ElementType = 'div'>(
  props: AsProp<T> & { imageSrc?: string; fallbackImageSrc?: string },
) {
  const { as, imageSrc, fallbackImageSrc, ...rest } = props;
  const Component = as || 'div';

  return (
    <Component
      style={{
        backgroundImage: `url(${imageSrc || fallbackImageSrc})`,
      }}
      className={styles.ImgAvatar}
      role="presentation"
      {...rest}
    />
  );
}

export default function UserAvatar<T extends ElementType = 'div'>({
  as,
  imageSrc,
  name,
  size = 'small',
  variant = 'rounded',
  kind = 'default',
  fallbackImageSrc,
}: AsProp<T> & UserAvatarProps): JSX.Element {
  const Component = as || 'div';
  const classes = classNames(styles.UserAvatar, [styles[kind]], {
    [styles[size]]: size,
    [styles[variant]]: variant,
  });

  const initials = getInitials(name);

  return (
    <Component className={classes}>
      {imageSrc ? (
        <AvatarImage imageSrc={imageSrc} fallbackImageSrc={fallbackImageSrc} />
      ) : fallbackImageSrc ? (
        <AvatarImage fallbackImageSrc={fallbackImageSrc} />
      ) : (
        initials
      )}
    </Component>
  );
}
